import React, { Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import ScrollToTop from './components/ScrollToTop';
import ScrollToSection from './components/ScrollToSection';
import LoadingSpinner from './components/common/LoadingSpinner';
import ErrorBoundary from './components/common/ErrorBoundary';
import { initializeErrorMonitoring } from './utils/monitoring-init';

// ルートベースのコード分割
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    margin: 0 !important;
    padding: 0 !important;
    font-family: 'Orbitron', 'Arial', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', monospace;
    background:
      linear-gradient(135deg, #000000 0%, #0a0a0a 30%, #111111 100%);
    color: #ffffff;
    overflow-x: hidden;
    min-height: 100vh;
  }

  ul {
    list-style: none;
  }

  /* ========================================
   * リンクのアクセシビリティ改善
   * ========================================
   * WCAG 2.1 レベルAA準拠（コントラスト比 4.5:1以上）
   * - 未訪問リンク: シアン（#00d4ff）- コントラスト比 8.59:1
   * - 訪問済みリンク: シアン（#00d4ff）- 未訪問と同じ色
   * - ホバー時: より明るいシアン + 光彩効果
   */
  a {
    color: #00d4ff; /* 明るいシアン - 高コントラスト */
    text-decoration: underline;
    text-decoration-color: rgba(0, 212, 255, 0.5);
    text-underline-offset: 3px;
    transition: all 0.3s ease;

    &:visited {
      color: #00d4ff; /* 未訪問と同じ色 */
      text-decoration-color: rgba(0, 212, 255, 0.5);
    }

    &:hover,
    &:focus {
      color: #33e0ff; /* より明るいシアン */
      text-decoration-color: #33e0ff;
      text-shadow: 0 0 10px rgba(51, 224, 255, 0.6);
      outline: 2px solid rgba(0, 212, 255, 0.3);
      outline-offset: 2px;
    }

    &:focus-visible {
      outline: 2px solid #00d4ff;
      outline-offset: 3px;
      border-radius: 2px;
    }
  }
`;

const App: React.FC = () => {
  // エラー監視サービスの初期化
  useEffect(() => {
    initializeErrorMonitoring();
  }, []);

  return (
    <>
      <GlobalStyle />
      <Router>
        <ScrollToTop />
        <ScrollToSection />
        <ErrorBoundary>
          <Suspense fallback={<LoadingSpinner message="ページを読み込み中..." />}>
            <Routes>
              <Route path="/" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </Router>
    </>
  );
};

export default App;